import { computed, Injectable } from '@angular/core';
import { UserService } from '../../../../shared/services/user.service';
import { ServiceOptions } from '../../constants/service-options.enum';
import { OrbitFormClaimService } from './orbit-form-claim.service';

export type ConfirmationPageType =
  | 'engineer'
  | 'call-service-provider'
  | 'collection'
  | 'self-send'
  | 'call-back'
  | 'pay-and-claim';

export type ExcessClaimsMessage = 'NO' | 'EXCESS' | 'CALLOUT';

@Injectable({
  providedIn: 'root',
})
export class GAService {
  constructor(
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly userService: UserService
  ) {}

  private viewModel = computed(() => {
    const bundle = this.orbitFormClaimService?.state?.claimData?.bundle;
    const mandatoryData = this.orbitFormClaimService?.state?.claimData?.mandatoryData;
    const userData = this.userService?.userInfo;
    return {
      digitalID: userData?.['custom:digital_id'],
      customerID: userData?.sub,
      modelNumber: mandatoryData?.modelNumber,
      serialNumber: mandatoryData?.serialNumber,
      productName: `${bundle?.manufacturer} ${bundle?.productType}`,
      manufacturer: bundle?.manufacturer,
      planNumber: bundle?.planNumber,
      underManufacturerGuarantee: mandatoryData?.planInWarrantyPeriod,
      claimedAccountBrand: mandatoryData?.bookingOEM,
      claimedClientName: mandatoryData?.bookingOEM,
      claimedBookingReference: this.orbitFormClaimService?.bookingData()?.claimID,
      bundle,
      mandatoryData,
      userData,
    };
  });

  get vm() {
    return this.viewModel();
  }

  GA_EVENTS = {
    PAGE_LOAD: 'spa-pageview',
    GENERIC: 'genericGAEvent',
  };

  PAGE_NAMES = {
    ERROR: 'claims-service-error',
    MODEL_NUMBER: 'claims-model-details',
    FAULT_CAPTURE: 'claims-fault-type-search',
    WHEN_CAUSE: 'claims-when-cause',
    CONFIRM_YOUR_DETAILS: 'claims-confirm-details',
    BOOK_ENGINEER: 'claims-book-engineer',
    BOOK_COLLECTION: 'claims-book-collection',
    ENGINEER_CONFIRMATION: 'claims-engineer-confirmation',
    CALL_SERVICE_PROVIDER_CONFIRMATION: 'claims-call-service-provider-confirmation',
    COLLECTION_CONFIRMATION: 'claims-collection-courier-confirmation',
    SELF_SEND_CONFIRMATION: 'claims-self-send-confirmation',
    CALL_BACK_CONFIRMATION: 'claims-call-back-confirmation',
    PAY_AND_CLAIM_CONFIRMATION: 'claims-pay-and-claim-confirmation',
  };

  sendGATag(GAObject: object) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(GAObject);
  }

  KEY_GA_INFO = {
    CATEGORY: 'claims-2.0',
    CLIENT_NAME: 'DG Claims',
  };

  // shared data for initial pageviews
  PAGE_VIEW = computed(() => ({
    event: this.GA_EVENTS.PAGE_LOAD,
    category: this.KEY_GA_INFO.CATEGORY,
    clientName: this.KEY_GA_INFO.CLIENT_NAME,
    DigitalID: this.vm.digitalID,
    customerID: this.vm.customerID,
    UnderManufacturerGuarantee: this.vm.underManufacturerGuarantee,
    claimGroup: this.vm.bundle?.claimType === 'SERVICE' ? 'asv' : 'dgx',
    claimedAccountBrand: this.vm.claimedAccountBrand,
    claimedClientName: this.vm.claimedClientName,
    claimedManufacturer: this.vm.manufacturer,
    claimedModelNumber: this.vm.modelNumber,
    claimedSerialNumber: this.vm.serialNumber,
    claimsLogin: 'my account',
    excessClaimsMessage: 'NO',
    planNumber: this.vm.planNumber,
    productName: this.vm.productName,
  }));

  // shared data for CTA click events
  GENERIC_EVENT = computed(() => ({
    event: this.GA_EVENTS.GENERIC,
    category: this.KEY_GA_INFO.CATEGORY,
    clientName: this.KEY_GA_INFO.CLIENT_NAME,
    planNumber: this.vm.planNumber,
    productModel: this.vm.modelNumber,
    productName: this.vm.productName,
    DigitalID: this.vm.digitalID,
    customerID: this.vm.customerID,
    claimGroup: this.vm.bundle?.claimType === 'SERVICE' ? 'asv' : 'dgx',
  }));

  GA_ERROR = {
    PAGE_LOAD: computed(() => ({
      pagename: this.PAGE_NAMES.ERROR,
      ...this.PAGE_VIEW(),
    })),
    EVENT: computed(() => ({
      pagename: this.PAGE_NAMES.ERROR,
      event: this.GA_EVENTS.GENERIC,
      category: this.KEY_GA_INFO.CATEGORY,
      clientName: this.KEY_GA_INFO.CLIENT_NAME,
      eventCategory: 'claims errors',
      eventAction: 'error codes',
    })),
  };

  GA_MODEL_NUMBER = {
    PAGE_LOAD: computed(() => ({
      pagename: this.PAGE_NAMES.MODEL_NUMBER,
      ...this.PAGE_VIEW(),
    })),
    WHERE_TO_FIND_NUMBER_CLICKED: computed(() => ({
      pagename: this.PAGE_NAMES.MODEL_NUMBER,
      ...this.GENERIC_EVENT(),
      eventCategory: 'claims model details',
      eventAction: 'where to find the number',
      eventLabel: 'CTA clicked',
    })),
  };

  GA_FAULT_CAPTURE = {
    PAGE_LOAD: computed(() => ({
      pagename: this.PAGE_NAMES.FAULT_CAPTURE,
      ...this.PAGE_VIEW(),
    })),
  };

  GA_WHEN_CAUSE = {
    PAGE_LOAD: computed(() => ({
      pagename: this.PAGE_NAMES.WHEN_CAUSE,
      ...this.PAGE_VIEW(),
    })),
  };

  GA_CONFIRM_YOUR_DETAILS = {
    PAGE_LOAD: computed(() => ({
      pagename: this.PAGE_NAMES.CONFIRM_YOUR_DETAILS,
      ...this.PAGE_VIEW(),
    })),
  };

  GA_BOOK_ENGINEER = {
    PAGE_LOAD: computed(() => ({
      pagename: this.PAGE_NAMES.BOOK_ENGINEER,
      ...this.PAGE_VIEW(),
      claimedBookingReference: this.vm.claimedBookingReference || '',
    })),
    EVENT_FIRST_DATE: computed(() => ({
      pagename: this.PAGE_NAMES.BOOK_ENGINEER,
      ...this.PAGE_VIEW(),
      event: this.GA_EVENTS.GENERIC,
      claimedBookingReference: this.vm.claimedBookingReference || '',
      eventCategory: 'claims book engineer',
      eventAction: 'first date shown',
    })),
    EVENT_DAYS_AVAILABLE: computed(() => ({
      pagename: this.PAGE_NAMES.BOOK_ENGINEER,
      ...this.PAGE_VIEW(),
      event: this.GA_EVENTS.GENERIC,
      claimedBookingReference: this.vm.claimedBookingReference || '',
      eventCategory: 'claims book engineer',
      eventAction: 'number-of-days-available',
    })),
    CALENDAR_BLOCK: computed(() => ({
      pagename: this.PAGE_NAMES.BOOK_ENGINEER,
      ...this.PAGE_VIEW(),
      event: this.GA_EVENTS.GENERIC,
      claimedBookingReference: this.vm.claimedBookingReference || '',
      eventCategory: 'claims book engineer',
      eventAction: 'calendar-block',
    })),
  };

  CONFIRMATION_PAGE_CONFIG: Record<
    ConfirmationPageType,
    { pagename: string; optionType: string; repairMethod: string; eventLabel: string }
  > = {
    engineer: {
      pagename: this.PAGE_NAMES.ENGINEER_CONFIRMATION,
      optionType: ServiceOptions.HomeRepair,
      repairMethod: 'engineer',
      eventLabel: 'engineer',
    },
    'call-service-provider': {
      pagename: this.PAGE_NAMES.CALL_SERVICE_PROVIDER_CONFIRMATION,
      optionType: ServiceOptions.HomeRepair,
      repairMethod: 'engineer',
      eventLabel: 'call-service-provider',
    },
    collection: {
      pagename: this.PAGE_NAMES.COLLECTION_CONFIRMATION,
      optionType: ServiceOptions.Courier,
      repairMethod: 'collection',
      eventLabel: 'collection',
    },
    'self-send': {
      pagename: this.PAGE_NAMES.SELF_SEND_CONFIRMATION,
      optionType: ServiceOptions.SelfSend,
      repairMethod: 'self-send',
      eventLabel: 'self-send',
    },
    'call-back': {
      pagename: this.PAGE_NAMES.CALL_BACK_CONFIRMATION,
      optionType: ServiceOptions.ManualReferral,
      repairMethod: 'call-back',
      eventLabel: 'call-back',
    },
    'pay-and-claim': {
      pagename: this.PAGE_NAMES.PAY_AND_CLAIM_CONFIRMATION,
      optionType: ServiceOptions.PayAndClaim,
      repairMethod: 'pay-and-claim',
      eventLabel: 'pay-and-claim',
    },
  };

  getConfirmationGATags(pageType: ConfirmationPageType) {
    const config = this.CONFIRMATION_PAGE_CONFIG[pageType];

    return {
      PAGE_LOAD: () => ({
        pagename: config.pagename,
        ...this.PAGE_VIEW(),
        claimedBookingReference: this.vm.claimedBookingReference || '',
        optionType: config.optionType,
        repairMethod: config.repairMethod,
      }),
      CONFIRMATION_EVENT: () => ({
        pagename: config.pagename,
        ...this.PAGE_VIEW(),
        event: this.GA_EVENTS.GENERIC,
        eventCategory: 'claims confirmation',
        eventAction: 'claim confirmed',
        eventLabel: config.eventLabel,
        claimedBookingReference: this.vm.claimedBookingReference || '',
        optionType: config.optionType,
        repairMethod: config.repairMethod,
      }),
      MMR: () => ({
        pagename: config.pagename,
        ...this.PAGE_VIEW(),
        event: this.GA_EVENTS.GENERIC,
        eventCategory: 'claims confirmation',
        eventAction: 'claim confirmed CTA',
        eventLabel: 'Manage my Repair',
        claimedBookingReference: this.vm.claimedBookingReference || '',
        optionType: config.optionType,
        repairMethod: config.repairMethod,
      }),
    };
  }

  stepEvents: { [key: string]: { pagename: string; eventCategory: string; eventAction: string; eventLabel: string } } =
    {
      'model-number': {
        pagename: this.PAGE_NAMES.MODEL_NUMBER,
        eventCategory: 'claims model details',
        eventAction: 'model confirmed',
        eventLabel: 'Continue',
      },
      'fault-details': {
        pagename: this.PAGE_NAMES.FAULT_CAPTURE,
        eventCategory: 'claims fault type',
        eventAction: '',
        eventLabel: 'Continue',
      },
      'fault-details-smart-fix-embedded-panel': {
        pagename: 'claims-sf-chatbot-embedded',
        eventCategory: 'SF chatbot embedded',
        eventAction: 'banner chatbot',
        eventLabel: '',
      },
      'repair-details': {
        pagename: this.PAGE_NAMES.WHEN_CAUSE,
        eventCategory: 'claimsRequest-dgx',
        eventAction: 'dateStoppedWorking-dgx',
        eventLabel: '',
      },
      'confirm-your-details': {
        pagename: this.PAGE_NAMES.CONFIRM_YOUR_DETAILS,
        eventCategory: 'claims confirm details',
        eventAction: 'details confirmed',
        eventLabel: 'Pick a repair date',
      },
      'book-engineer': {
        pagename: this.PAGE_NAMES.BOOK_ENGINEER,
        eventCategory: 'claims book engineer',
        eventAction: 'book this time',
        eventLabel: '',
      },
      'book-collection': {
        pagename: this.PAGE_NAMES.BOOK_ENGINEER,
        eventCategory: 'claims book engineer',
        eventAction: 'book this time',
        eventLabel: '',
      },
      'product-number': {
        pagename: 'claims-product-number-code',
        eventCategory: 'claims product number code',
        eventAction: 'pnc confirmed',
        eventLabel: 'Continue',
      },
    };

  getContinueClickedData(step: string, eventLabel?: string) {
    const eventDetails = this.stepEvents[step];
    return {
      ...this.GENERIC_EVENT(),
      ...eventDetails,
      eventLabel: eventLabel || eventDetails?.eventLabel || '', // Use the passed eventLabel if provided
    };
  }
}
